import { createContext, useContext, useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { getEducation, preloadPortfolioData } from '../firebase';
import { useLoading } from './LoadingContext';
import { logError } from '../utils/error-handler';

const EducationContext = createContext(null);

export const EducationProvider = ({ children }) => {
  const [education, setEducation] = useState(getEducation() || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { finishTask, beginExpectedTasks } = useLoading();

  const loadEducation = useCallback(async () => {
    setLoading(true);
    setError(null);
    beginExpectedTasks(['education']);

    try {
      await preloadPortfolioData();
      const educationData = getEducation();
      setEducation(educationData || []);
    } catch (err) {
      logError(err, 'EducationProvider', {});
      setError(err);
    } finally {
      setLoading(false);
      finishTask('education');
    }
  }, [beginExpectedTasks, finishTask]);

  const switchLanguageClientSide = useCallback(() => {
    beginExpectedTasks(['education']);

    requestAnimationFrame(() => {
      try {
        const educationData = getEducation();
        setEducation(educationData || []);
      } catch (err) {
        logError(err, 'switchLanguageClientSide - Education', {});
        loadEducation();
      } finally {
        finishTask('education');
      }
    });
  }, [beginExpectedTasks, finishTask, loadEducation]);

  useEffect(() => {
    let isMounted = true;
    loadEducation();

    const handleLanguageChange = () => {
      if (isMounted) {
        switchLanguageClientSide();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [loadEducation, switchLanguageClientSide]);

  const sortedEducation = useMemo(() => {
    return Array.isArray(education)
      ? [...education].sort((a, b) => b.id - a.id)
      : [];
  }, [education]);

  const value = useMemo(
    () => ({
      education: sortedEducation,
      loading,
      error,
    }),
    [sortedEducation, loading, error]
  );

  return (
    <EducationContext.Provider value={value}>
      {children}
    </EducationContext.Provider>
  );
};

export const useEducation = () => {
  const context = useContext(EducationContext);
  if (!context) {
    throw new Error('useEducation must be used within EducationProvider');
  }
  return context;
};
